import System from '../System';
import Game from 'Game';
import { TaskEvents } from 'Utility/Task/TaskQueue';
import CleanupManager from 'Utility/CleanupManager';
import { IGameState } from 'State/Game/IGameState';
import Animator from 'Prototype/Animator';
import GameObject from 'GameObject/GameObject';
import { addDragonbonesResources } from 'Utility/Resource/ResourceHelper';
import { DRAGONBONES_PATH } from 'Constants/Dragonbones';
import FarmUISystem from 'System/UI/FarmUISystem';
import FarmRewardSyncTask from 'System/World/Task/FarmRewardSyncTask';
import FarmSyncTask from './Task/FarmSyncTask';
import { getFullFarmItemAssetDetails } from './FarmHelpers';
import { AUDIO_PACKS } from 'System/Audio';
import { ResourceDriver } from 'Utility/Resource/ResourceManager';
import { AUDIO_PATH, PlayMusic } from 'System/Audio/AudioHelper';
import { MUSIC_VOLUME_VALUE } from 'Constants/World';
import { FARM_EFFECT_DRAGONBONES_PATH } from 'Constants/Farm';
import { isFarmItemId } from 'System/Farm/Data/FarmItemData';

export default class FarmSystem extends System {
    private readonly cleanupManager = new CleanupManager();

    public animator: Animator;

    public init(): void {
        this.taskQueue.on(TaskEvents.START, this.tasksStarted.bind(this));
        this.taskQueue.on(TaskEvents.COMPLETE, this.tasksEnded.bind(this));
    }

    public async preload(): Promise<any> {
        // Load My-Farm sound effects using the secondary driver. If audio clips are played before the asset is loaded
        // they are ignored.
        Game.resource.add(AUDIO_PACKS.farmSystem, undefined, ResourceDriver.SECONDARY);

        // Load music with the primary resource driver as it must be loaded before the scene can start. This is a requirement
        // as asynchronously loading sounds and playing once they are loaded is not supported yet.
        Game.resource.add('farm_music', `${AUDIO_PATH}farm/farm_music.mp3`);

        Game.resource.add('atlas-my_farm', 'resource/graphics/sprite_sheets/my_farm.json');
        Game.resource.add('my_farm_bg', 'resource/graphics/other/my_farm_bg.jpg');
        Game.resource.add('farm_highlight', 'resource/graphics/other/farm_highlight.png');
        Game.resource.add('loading_farm_icon', 'resource/graphics/other/loading_farm_icon.png');
        addDragonbonesResources('my_farm_effects', FARM_EFFECT_DRAGONBONES_PATH);

        const itemPreload: Promise<any>[] = [];

        const state = Game.store.getState();
        for (let slotId in state.playerState.farmState.slots) {
            if (!slotId) continue;
            const itemId = state.playerState.farmState.slots[slotId]?.itemId;
            if (!itemId || !isFarmItemId(itemId)) continue;
            const assetDetails = getFullFarmItemAssetDetails(itemId);
            itemPreload.push(assetDetails.preload());
        }
        // Load secondary driver assets asynchronously. This should only be sound effects. Music is required.
        Game.resource.load(ResourceDriver.SECONDARY);

        addDragonbonesResources('my_farm_slot_animations', `${DRAGONBONES_PATH}/Farm/`);

        return Promise.all([Game.resource.load(), ...itemPreload]);
    }

    public onUpdate(): void { }

    public onStart(): void {
        this.animator = GameObject.Instantiate(Animator);
        this.scene.addGameObject(this.animator);

        PlayMusic('farm_music', {
            volume: MUSIC_VOLUME_VALUE,
        });
    }

    public onDestroy(): void {
        this.taskQueue.off(TaskEvents.START, this.tasksStarted.bind(this));
        this.taskQueue.off(TaskEvents.COMPLETE, this.tasksEnded.bind(this));
    }

    public handleStateChanged(state: IGameState) {
        this.checkQueue(state);
    }

    private checkQueue(state: IGameState) {
        if (!this.isActive() && (!state.gameLoaded || (state.entryPoint?.entryPoint && !state.entryPoint.handled)))
            return;

        if (state.playerState.tasks.length === 0) return;

        if (!this.taskQueue.some(task => task instanceof FarmRewardSyncTask))
            this.taskQueue.queueNext(new FarmRewardSyncTask());

        if (!this.taskQueue.some(task => task instanceof FarmSyncTask)) this.taskQueue.queue(new FarmSyncTask());

        if (state.UIState.popupID === null) {
            this.taskQueue.start(this);
        }
    }

    public getRewardContainer(): GameObject {
        const farmUISystem = this.scene.getSystem(FarmUISystem);
        return farmUISystem.hud.rewardContainer;
    }

    protected onLateUpdate(): void { }

    protected onStop(): void {
        this.cleanupManager.cleanUpAll();
    }

    protected tasksStarted(): void {
        this.scene.emit(TaskEvents.START);
    }

    protected tasksEnded(): void {
        this.scene.emit(TaskEvents.COMPLETE);
    }
}
